<script setup lang="ts">
/**
 * CoreField
 *
 * Wrapper component for form controls.
 *
 * Responsibilities:
 * - A11y wiring: generates/chooses a stable `id`, renders `<label for>`, and
 *   exposes `id` + `aria-describedby` via a scoped slot so the control can wire
 *   itself correctly.
 * - Consistent layout: label, control, and details (description/errors) render
 *   in a stable structure so forms look consistent without per-field CSS.
 * - Message rendering: description + errors rendered with stable element IDs:
 *   `${id}__desc` and `${id}__err`.
 *
 * Non-responsibilities:
 * - It does not render an input itself; field components do that.
 * - It does not evaluate rules; field components decide what `errorMessages`
 *   to pass based on touch/submitted state.
 */
import { computed, toRef } from 'vue'
import type { CoreErrorMessages, CoreFieldDensity, CoreFieldVariant } from './types'
import { useStableId } from './useStableId'
import { normalizeErrorMessages } from './useRulesValidation'

const props = withDefaults(
  defineProps<{
    id?: string
    name?: string
    label?: string
    description?: string
    errorMessages?: CoreErrorMessages
    required?: boolean
    disabled?: boolean
    readonly?: boolean
    variant?: CoreFieldVariant
    density?: CoreFieldDensity
    hideDetails?: 'auto' | 'always' | 'never'
  }>(),
  {
    id: undefined,
    name: undefined,
    label: undefined,
    description: undefined,
    errorMessages: undefined,
    required: false,
    disabled: false,
    readonly: false,
    variant: 'outlined',
    density: 'default',
    hideDetails: 'auto',
  },
)

defineSlots<{
  default(props: {
    id: string
    describedBy: string | undefined
    invalid: boolean
    disabled: boolean
    readonly: boolean
    required: boolean
  }): unknown
}>()

const id = useStableId({
  id: toRef(props, 'id'),
  name: toRef(props, 'name'),
  prefix: 'core-field',
})

const descriptionId = computed(() => `${id.value}__desc`)
const errorsId = computed(() => `${id.value}__err`)

const normalizedErrors = computed(() => normalizeErrorMessages(props.errorMessages))
const hasErrors = computed(() => normalizedErrors.value.length > 0)
const invalid = computed(() => hasErrors.value)

const showDescription = computed(() => Boolean(props.description?.trim()))
const showErrors = computed(() => hasErrors.value)
const showDetails = computed(() => props.hideDetails !== 'never')

const describedBy = computed(() => {
  if (!showDetails.value) return undefined
  const ids: string[] = []
  if (showDescription.value) ids.push(descriptionId.value)
  if (showErrors.value) ids.push(errorsId.value)
  return ids.length ? ids.join(' ') : undefined
})

const detailsMode = computed(() => props.hideDetails)
</script>

<template>
  <div class="core-field">
    <label
      v-if="label"
      class="block mb-1 text-sm font-medium text-on-surface"
      :for="id"
    >
      {{ label }}
      <span v-if="required" class="text-on-surface-variant"> *</span>
    </label>

    <slot
      :id="id"
      :describedBy="describedBy"
      :invalid="invalid"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
    />

    <div v-if="showDetails" class="mt-1 text-xs">
      <p
        v-if="showDescription"
        :id="descriptionId"
        class="text-on-surface-variant"
      >
        {{ description }}
      </p>

      <ul
        v-if="showErrors"
        :id="errorsId"
        class="text-danger"
      >
        <li v-for="(msg, idx) in normalizedErrors" :key="`${idx}-${msg}`">
          {{ msg }}
        </li>
      </ul>

      <div
        v-if="!showDescription && !showErrors && (detailsMode === 'auto' || detailsMode === 'always')"
        class="min-h-4"
        aria-hidden="true"
      />
    </div>
  </div>
</template>
